import { useNurionTheme } from "@nurion/theme/react";
import {
  ArrowUpRight,
  Camera,
  ChevronRight,
  KeyRound,
  LogOut,
  Moon,
  ShieldCheck,
  Smile,
  Sun,
  SunMoon,
  UserX,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { BlockedPeople } from "~/components/BlockedPeople";
import { Dialog } from "~/components/Dialog";
import { DeleteAccountSheet, SHEET_PANEL_STYLE } from "~/components/mobile/DeleteAccountSheet";
import { MobileActionSheet } from "~/components/mobile/MobileActionSheet";
import { mobileStrings } from "~/components/mobile/strings";
import { SettingsDialog } from "~/components/SettingsDialog";
import { StatusPicker } from "~/components/StatusPicker";
import { useEmbedRuntime } from "~/context/EmbedRuntimeContext";
import { useMatrix } from "~/context/MatrixContext";
import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { useMyStatus } from "~/hooks/useMyStatus";
import { useOwnMemberNames } from "~/hooks/useOwnMemberNames";
import { useOwnAvatar } from "~/hooks/useOwnAvatar";
import { localpartOf, resolveAccountLabel } from "~/lib/account-label";
import { getInitials } from "~/lib/initials";
import type { Client } from "~/lib/matrix";
import { subscribeBlockedUsers } from "~/lib/matrix";
import { openExternal, platform } from "~/lib/platform";
import { ICON_STROKE } from "~/lib/icon";

export const PRIVACY_URL = "https://nurion.com/legal/privacy";
export const TERMS_URL = "https://nurion.com/legal/terms";

export interface MobileYouProps {
  onSignOut: () => void;
}

type ThemeOption = "system" | "light" | "dark";

const THEME_ICON: Record<ThemeOption, LucideIcon> = { system: SunMoon, light: Sun, dark: Moon };

export function MobileYou({ onSignOut }: MobileYouProps) {
  const { client, displayName, userId, canUnlockHistory, resumeRecovery } = useMatrix();
  const { consoleUrl } = useEmbedRuntime();
  const { choice, setTheme } = useNurionTheme();
  const { status, setStatus } = useMyStatus(client);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [choosingTheme, setChoosingTheme] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(false);
  const photo = useOwnAvatar(client, userId);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [blockedCount, setBlockedCount] = useState(0);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const statusRef = useRef<HTMLButtonElement | null>(null);
  const statusId = useId();
  const titleId = useId();
  const bodyId = useId();
  const localpart = localpartOf(userId);
  const { names } = useOwnMemberNames(true);
  const name =
    resolveAccountLabel({
      accountName: null,
      globalDisplayName: displayName,
      memberNames: names,
      localpart,
    }) ??
    userId ??
    "";
  const initials = getInitials(name) || "–";
  const username = userId?.replace(/^@/, "").split(":")[0] ?? "";
  const theme: ThemeOption = choice ?? "system";

  useEffect(() => {
    if (!client) return;
    return subscribeBlockedUsers(client, (ids) => setBlockedCount(ids.length));
  }, [client]);

  useEffect(() => {
    if (platform !== "tauri") return;
    let cancelled = false;
    void import("@tauri-apps/api/app")
      .then((app) => app.getVersion())
      .then((next) => {
        if (!cancelled) setVersion(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const open = (url: string) => {
    void openExternal(url).catch((err) => console.warn("openExternal failed", err));
  };

  const confirmSignOut = () => {
    setConfirmingSignOut(false);
    onSignOut();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-6">
      <div className="flex flex-col items-center px-4 pt-8 text-center">
        <button
          type="button"
          aria-label={photo.mxc ? mobileStrings.you.photo.change : mobileStrings.you.photo.add}
          aria-busy={photo.busy}
          disabled={photo.busy}
          onClick={() => (photo.mxc ? setEditingPhoto(true) : photoInputRef.current?.click())}
          className="relative rounded-full outline-none focus-visible:shadow-neon-cyan"
        >
          <ProfilePhoto client={client} mxc={photo.mxc} initials={initials} busy={photo.busy} />
          <span
            aria-hidden
            className="absolute bottom-0 right-0 flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-foreground/80"
          >
            <Camera size={14} strokeWidth={ICON_STROKE} />
          </span>
        </button>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void photo.choose(file);
          }}
        />
        {photo.failed && (
          <p role="alert" className="mt-2 text-xs font-light text-destructive-text">
            {mobileStrings.you.photo.failed}
          </p>
        )}
        <h1 className="mt-4 max-w-full truncate text-xl font-light text-foreground/90">{name}</h1>
        {userId && userId !== name && (
          <span className="mt-1 max-w-full truncate text-sm font-light text-muted-foreground">
            {userId}
          </span>
        )}
        <button
          ref={statusRef}
          id={statusId}
          type="button"
          onClick={() => setStatusOpen(true)}
          className="mt-3 flex h-11 max-w-full items-center gap-2 rounded-full px-4 outline-none transition-colors hover:bg-foreground/[0.03] active:bg-foreground/[0.06] focus-visible:shadow-neon-cyan"
        >
          <span
            aria-hidden
            className="flex size-5 shrink-0 items-center justify-center text-base text-muted-foreground"
          >
            {status?.emoji || <Smile size={16} strokeWidth={ICON_STROKE} />}
          </span>
          <span
            className={`min-w-0 truncate text-sm font-light ${
              status ? "text-foreground/90" : "text-muted-foreground"
            }`}
          >
            {status?.message || mobileStrings.you.setStatus}
          </span>
        </button>
      </div>
      {statusOpen && (
        <StatusPicker
          anchor={statusRef.current}
          status={status}
          onSetStatus={setStatus}
          onClear={() => setStatus(null)}
          onClose={() => setStatusOpen(false)}
          labelledBy={statusId}
        />
      )}

      <Group>
        {canUnlockHistory && (
          <Row
            icon={KeyRound}
            label={mobileStrings.you.unlock}
            trailing={<span aria-hidden className="size-2 shrink-0 rounded-full bg-secondary" />}
            onSelect={resumeRecovery}
          />
        )}
        <Row
          icon={ShieldCheck}
          label={mobileStrings.you.encryption}
          trailing={<Chevron />}
          onSelect={() => setSettingsOpen(true)}
        />
        <Row
          icon={THEME_ICON[theme]}
          label={mobileStrings.you.appearance}
          trailing={
            <span className="text-sm font-light text-muted-foreground">
              {mobileStrings.you.theme[theme]}
            </span>
          }
          onSelect={() => setChoosingTheme(true)}
        />
        {blockedCount > 0 && (
          <Row
            icon={UserX}
            label={mobileStrings.you.blockedPeople}
            trailing={
              <span className="flex items-center gap-1 text-sm font-light text-muted-foreground">
                {blockedCount}
                <Chevron />
              </span>
            }
            onSelect={() => setBlockedOpen(true)}
          />
        )}
      </Group>

      <Group>
        <Row
          icon={LogOut}
          label={mobileStrings.you.signOut}
          onSelect={() => setConfirmingSignOut(true)}
        />
      </Group>

      <div className="mt-12 flex flex-col">
        <FootRow label={mobileStrings.you.privacy} onSelect={() => open(PRIVACY_URL)}>
          <External />
        </FootRow>
        <FootRow label={mobileStrings.you.terms} onSelect={() => open(TERMS_URL)}>
          <External />
        </FootRow>
        <FootRow
          label={mobileStrings.you.deleteAccount}
          onSelect={() => setConfirmingDelete(true)}
        />
        {version && (
          <p className="px-4 pt-2 text-xs font-light text-muted-quiet">
            {mobileStrings.you.version(version)}
          </p>
        )}
      </div>

      {confirmingDelete && (
        <DeleteAccountSheet
          username={username}
          url={`${consoleUrl.replace(/\/$/, "")}/account`}
          onClose={() => setConfirmingDelete(false)}
        />
      )}

      {settingsOpen && (
        <SettingsDialog
          title={mobileStrings.you.encryption}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {editingPhoto && (
        <MobileActionSheet
          label={mobileStrings.you.photo.change}
          actions={[
            {
              key: "choose",
              label: mobileStrings.you.photo.choose,
              onSelect: () => photoInputRef.current?.click(),
            },
            {
              key: "remove",
              label: mobileStrings.you.photo.remove,
              destructive: true,
              onSelect: () => void photo.remove(),
            },
          ]}
          onClose={() => setEditingPhoto(false)}
        />
      )}
      {blockedOpen && blockedCount > 0 && (
        <Dialog
          align="bottom"
          label={mobileStrings.you.blockedPeople}
          onClose={() => setBlockedOpen(false)}
          panelStyle={{
            width: "100%",
            maxWidth: "100%",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <div className="px-5 py-5">
            <BlockedPeople />
          </div>
        </Dialog>
      )}
      {choosingTheme && (
        <MobileActionSheet
          label={mobileStrings.you.appearance}
          actions={(["system", "light", "dark"] as const).map((option) => ({
            key: option,
            label: mobileStrings.you.theme[option],
            selected: option === theme,
            onSelect: () => setTheme(option === "system" ? null : option),
          }))}
          onClose={() => setChoosingTheme(false)}
        />
      )}
      {confirmingSignOut && (
        <Dialog
          align="bottom"
          labelledBy={titleId}
          describedBy={bodyId}
          onClose={() => setConfirmingSignOut(false)}
          panelStyle={SHEET_PANEL_STYLE}
        >
          <div className="flex flex-col gap-5 px-5 py-5">
            <div className="space-y-2">
              <h2 id={titleId} className="text-base font-light text-foreground/90">
                {mobileStrings.you.signOutTitle}
              </h2>
              <p id={bodyId} className="text-sm font-light leading-6 text-muted-foreground">
                {mobileStrings.you.signOutBody}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={confirmSignOut}
                className="h-11 rounded-lg bg-foreground text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
              >
                {mobileStrings.you.signOut}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingSignOut(false)}
                className="h-11 text-sm font-light text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:shadow-neon-cyan"
              >
                {mobileStrings.you.cancelSignOut}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function ProfilePhoto({
  client,
  mxc,
  initials,
  busy,
}: {
  client: Client | null;
  mxc: string | null;
  initials: string;
  busy: boolean;
}) {
  const media = useAuthenticatedMedia(client, mxc, {
    thumbnail: { w: 160, h: 160, method: "crop" },
  });
  return (
    <span
      aria-hidden
      className={`flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-foreground/[0.1] text-xl font-light text-foreground/80 transition-opacity ${
        busy ? "animate-pulse opacity-50" : ""
      }`}
    >
      {media.src ? <img src={media.src} alt="" className="size-full object-cover" /> : initials}
    </span>
  );
}

function Group({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 flex flex-col">{children}</div>;
}

function Row({
  icon: Icon,
  label,
  trailing,
  onSelect,
}: {
  icon: LucideIcon;
  label: string;
  trailing?: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex h-12 items-center gap-3 px-4 text-left outline-none transition-colors hover:bg-foreground/[0.03] active:bg-foreground/[0.06] focus-visible:shadow-neon-cyan"
    >
      <span
        aria-hidden
        className="flex size-6 shrink-0 items-center justify-center text-muted-foreground"
      >
        <Icon size={18} strokeWidth={ICON_STROKE} />
      </span>
      <span className="min-w-0 flex-1 truncate text-base font-light text-foreground/90">
        {label}
      </span>
      {trailing}
    </button>
  );
}

function FootRow({
  label,
  onSelect,
  children,
}: {
  label: string;
  onSelect: () => void;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex h-11 items-center gap-1.5 px-4 text-left text-sm font-light text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:shadow-neon-cyan"
    >
      {label}
      {children}
    </button>
  );
}

function Chevron() {
  return (
    <ChevronRight
      aria-hidden
      size={16}
      strokeWidth={ICON_STROKE}
      className="shrink-0 text-muted-quiet"
    />
  );
}

function External() {
  return (
    <ArrowUpRight
      aria-hidden
      size={16}
      strokeWidth={ICON_STROKE}
      className="shrink-0 text-muted-quiet"
    />
  );
}
